/**
 * Fendertex calculator — product catalogue + live storefront lookups.
 *
 * Layer A: static colour → product handle table (kept in sync with
 *   inventory under fendertex.nl). Never references *-copy products.
 * Layer B: live fetch of /products/{handle}.js to resolve a model-code
 *   to a variant id and price. No prices are stored statically.
 */
(function () {
  // 12-colour master palette. Handles per accessory map to a subset of these.
  // Order + hexes match the Figma hero swatch row (frame 2123:4045).
  const PALETTE = [
    { key: 'black',      label: 'Black',      hex: '#0a0a0a' },
    { key: 'dark-grey',  label: 'Dark Grey',  hex: '#474747' },
    { key: 'grey',       label: 'Grey',       hex: '#b9bab9' },
    { key: 'navy-blue',  label: 'Navy Blue',  hex: '#041e48' },
    { key: 'royal-blue', label: 'Royal Blue', hex: '#014b95' },
    { key: 'beige',      label: 'Beige',      hex: '#b19863' },
    { key: 'burgundy',   label: 'Burgundy',   hex: '#692726' },
    { key: 'red',        label: 'Red',        hex: '#a60101' },
    { key: 'orange',     label: 'Orange',     hex: '#f46603' },
    { key: 'lagoon',     label: 'Lagoon',     hex: '#0bb9a8' },
    { key: 'yellow',     label: 'Yellow',     hex: '#c98904' },
    { key: 'green',      label: 'Green',      hex: '#015011' },
  ];

  // colour key -> product handle. null = not stocked in that colour.
  // c-fender-burgendy typo deliberately retained (matches the live product handle).
  const HANDLES = {
    cylindrical: {
      'black':      'black-fender',
      'grey':       'grey-fender',
      'dark-grey':  'c-fender-dark-grey',
      'navy-blue':  'black-fender-navy-blue',
      'beige':      'c-fender-beige',
      'burgundy':   'c-fender-burgendy',
      'green':      'c-fender-green',
      'lagoon':     'c-fender-lagoon',
      'orange':     'c-fender-orange',
      'red':        'c-fender-red',
      'royal-blue': 'c-fender-royal-blue',
      'yellow':     'c-fender-yellow',
    },
    spherical: {
      'black':      's-fender-black',
      'grey':       's-fender-grey',
      'dark-grey':  's-fender-dark-grey',
      'navy-blue':  's-fender-navy-blue',
      'beige':      's-fender-beige',
      'burgundy':   's-fender-burgundy',
      'green':      's-fender-green',
      'lagoon':     's-fender-lagoon',
      'orange':     's-fender-orange',
      'red':        's-fender-red',
      'royal-blue': 's-fender-royal-blue',
      'yellow':     's-fender-yellow',
    },
    cCover: {
      'black':      'cylinder-cover-black',
      'grey':       'cylinder-cover-grey',
      'dark-grey':  null,
      'navy-blue':  'cylinder-cover-navy-blue',
      'beige':      'c-cover-beige',
      'burgundy':   'c-cover-burgundy',
      'green':      'c-cover-green',
      'lagoon':     'c-cover-lagoon',
      'orange':     'c-cover-orange',
      'red':        'c-cover-red',
      'royal-blue': 'c-cover-royal-blue',
      'yellow':     'c-cover-yellow',
    },
    sCover: {
      'black':      's-cover-black',
      'grey':       's-cover-grey',
      'dark-grey':  's-cover-dark-grey',
      'navy-blue':  's-cover-navy-blue',
      'beige':      's-cover-beige',
      'burgundy':   's-cover-burgundy',
      'green':      's-cover-green',
      'lagoon':     's-cover-lagoon',
      'orange':     's-cover-orange',
      'red':        's-cover-red',
      'royal-blue': 's-cover-royal-blue',
      'yellow':     's-cover-yellow',
    },
    clsCover: {
      'black':      'cylinder-long-side-cover-black',
      'grey':       'cylinder-long-side-cover-grey',
      'dark-grey':  null,
      'navy-blue':  'cylinder-long-side-cover-navy-blue',
      'beige':      'cls-cover-beige',
      'burgundy':   'cls-cover-burgundy',
      'green':      'cls-cover-green',
      'lagoon':     'cls-cover-lagoon',
      'orange':     'cls-cover-orange',
      'red':        'cls-cover-red',
      'royal-blue': 'cls-cover-royal-blue',
      'yellow':     'cls-cover-yellow',
    },
    rope: {
      'black':      'rope-black',
      'grey':       'rope-grey',
      'dark-grey':  'rope-dark-grey',
      'navy-blue':  'rope-navy-blue',
      'beige':      'rope-beige',
      'burgundy':   'rope-burgundy',
      'green':      'rope-green',
      'lagoon':     null,
      'orange':     'rope-orange',
      'red':        'rope-red',
      'royal-blue': 'rope-royal-blue',
      'yellow':     null,
    },
    dRing: {
      'black':      'sheated-d-ring-black',
      'grey':       'sheated-d-ring-grey',
      'dark-grey':  'sheated-d-ring-dark-grey',
      'navy-blue':  'sheated-d-ring-navy-blue',
      'beige':      'sheated-d-ring-beige',
      'burgundy':   'sheated-d-ring-burgundy',
      'green':      'sheated-d-ring-green',
      'lagoon':     null,
      'orange':     'sheated-d-ring-orange',
      'red':        'sheated-d-ring-red',
      'royal-blue': 'sheated-d-ring-royal-blue',
      'yellow':     null,
    },
    // Single-product accessories (not colour-grouped).
    pumpGm5:  'manual-pump-gm5',
    pumpGe10: 'ge-10-electric-pump',
  };

  // Rope: which variant to pre-select. Variant title format: "<length> / <Yes|No>"
  // (length = "1m"…"5m"; Yes/No = spliced loop). Default to the spliced variant.
  const ROPE_DEFAULT_LENGTH = '3m';
  const ROPE_DEFAULT_LOOP = 'Yes';

  // /products/{handle}.js base — same origin when running inside the storefront,
  // otherwise fall back to fendertex.nl (whose endpoints allow CORS).
  function productBase() {
    if (typeof window !== 'undefined' && window.Shopify) return '';
    return 'https://fendertex.nl';
  }

  const cache = new Map();

  async function fetchProduct(handle) {
    if (!handle) throw new Error('fetchProduct: missing handle');
    if (cache.has(handle)) return cache.get(handle);
    const url = productBase() + '/products/' + handle + '.js';
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('fetchProduct ' + handle + ': HTTP ' + res.status);
    const data = await res.json();
    cache.set(handle, data);
    return data;
  }

  // The variant title in fender/cover products starts with the model code:
  //   "C84 (30 x 80cm)"   for C-Fender
  //   "C84"               for C-Cover, CLS-Cover (and S-Cover for spheres)
  // Match by leading token.
  function pickVariantByModelCode(product, modelCode) {
    return product.variants.find(function (v) {
      const head = String(v.title).split(/[\s/]+/)[0];
      return head === modelCode;
    });
  }

  function pickRopeVariant(product, length, loop) {
    return product.variants.find(function (v) { return v.title === length + ' / ' + loop; });
  }

  function pickSoleVariant(product) {
    return product.variants[0];
  }

  function priceCentsToString(cents, currency) {
    const amount = (cents / 100).toFixed(2);
    const sym = currency === 'EUR' ? '€' : (currency ? currency + ' ' : '');
    return sym + amount;
  }

  const FenderProducts = {
    PALETTE,
    HANDLES,
    ROPE_DEFAULT_LENGTH,
    ROPE_DEFAULT_LOOP,
    fetchProduct,
    pickVariantByModelCode,
    pickRopeVariant,
    pickSoleVariant,
    priceCentsToString,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = FenderProducts;
  if (typeof window !== 'undefined') window.FenderProducts = FenderProducts;
})();
